import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getSupabase } from '@/lib/supabase';
import { clearAuthPending } from '@/lib/authPending';
import { useAuthStore } from '@/store/auth';
import { useNav } from '@/lib/nav';
import { postAuthChanged, onAuthChanged } from '@/lib/authChannel';
import { queryClient } from '@/lib/queryClient';
import routes from '@/lib/routes';

function stripSupabaseAuthFromHash(): void {
  try {
    const currentHash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!currentHash) return;

    const raw = currentHash.startsWith('#') ? currentHash.slice(1) : currentHash;
    if (!raw) return;

    const AUTH_KEYS = [
      'access_token','refresh_token','type','expires_at','expires_in','token_type',
      'provider','code','error','error_code','error_description','redirect_to'
    ];

    const segments = raw.split('&');
    let mutated = false;
    const kept: string[] = [];
    for (const seg of segments) {
      if (!seg) continue;
      const eqIdx = seg.indexOf('=');
      const key = decodeURIComponent(eqIdx >= 0 ? seg.slice(0, eqIdx) : seg);
      if (AUTH_KEYS.includes(key)) {
        mutated = true;
        continue;
      }
      kept.push(seg);
    }

    if (!mutated) return;

    const nextHash = kept.join('&');
    const url = new URL(window.location.href);
    const next = url.origin + url.pathname + url.search + (nextHash ? ('#' + nextHash) : '');
    // One-time instrumentation to ensure cleanup happens post-auth processing
    try { console.count && console.count('stripSupabaseAuthFromHash'); } catch {}
    window.history.replaceState({}, '', next);
  } catch {}
}

type AuthContextValue = {
  initializing: boolean;
  user: ReturnType<typeof useAuthStore.getState>['user'];
  session: ReturnType<typeof useAuthStore.getState>['session'];
  defaultProjectId: string | null;
  authError: string | null;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [initializing, setInitializing] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const setSession = useAuthStore((s) => s.setSession);
  const setFromSession = useAuthStore((s) => s.setFromSession);
  const setReady = useAuthStore((s) => s.setReady);
  const user = useAuthStore((s) => s.user);
  const session = useAuthStore((s) => s.session);
  const defaultProjectId = useAuthStore((s) => s.defaultProjectId);
  const { go } = useNav();
  const hasBootRedirectedRef = useRef(false);
  const bootPhaseRef = useRef(true);
  const ran = useRef(false); // StrictMode run-once guard

  const postAuthNav = useCallback(() => {
    try {
      // Do not run internal post-auth navigation while on public routes that should not be overridden
      try {
        const p = window.location.pathname || '';
        // Skip redirect for public routes
        if (p.startsWith('/blog') || p.startsWith('/help') || p.startsWith('/share') || 
            p.startsWith('/legal/') || p === '/terms' || p === '/privacy' || p === '/data') return;
        // Skip redirect if already on a valid authenticated route
        if (p === '/dashboard' || 
            p === '/account' ||
            p.startsWith('/project/') ||
            p.startsWith('/plot-editor/') ||
            p.startsWith('/billing/')) return;
      } catch {}
      const saved = localStorage.getItem('returnTo');
      if (saved) localStorage.removeItem('returnTo');
      // Treat home ('/') as no-op: fall back to editor
      const useSaved = (() => {
        try {
          if (!saved) return null;
          const url = new URL(saved, window.location.origin);
          return url.pathname && url.pathname !== '/' ? saved : null;
        } catch { return null; }
      })();
      go(useSaved || routes.plotEditor.new());
    } catch {
      go(routes.plotEditor.new());
    }
  }, [go]);

  useEffect(() => {
    let unsub: { data: { subscription: { unsubscribe: () => void } } } | null = null;

    const API_BASE = ((import.meta as any).env?.VITE_API_BASE_URL as string) || '';
    const apiUrl = (p: string) => (API_BASE ? `${API_BASE.replace(/\/$/, '')}${p}` : p);

    async function fetchServerSessionFromToken(accessToken: string) {
      try {
        const res = await fetch(apiUrl('/api/auth/session'), {
          headers: { Authorization: `Bearer ${accessToken}` },
          credentials: 'include',
        });
        if (res.status === 401) {
          setSession(null);
          setAuthError('Auth token was rejected by /api/auth/session (401). Make sure the API server is running with matching Supabase keys.');
          try { clearAuthPending(); } catch {}
          return null;
        }
        if (!res.ok) {
          const txt = await res.text();
          throw new Error(`${res.status}: ${txt}`);
        }
        const payload = await res.json();
        setSession(payload);
        setAuthError(null);
        try { if (payload) clearAuthPending(); } catch {}
        return payload;
      } catch (error: any) {
        setSession(null);
        setAuthError(error?.message || 'Failed to contact /api/auth/session. Check API server connectivity.');
        try { clearAuthPending(); } catch {}
        return null;
      }
    }

    (async () => {
      if (ran.current) return; ran.current = true;
      try {
        const sb = getSupabase();
        // Explicitly exchange OAuth code for a session if present (PKCE)
        try {
          const url = new URL(window.location.href);
          if (url.searchParams.has('code')) {
            await sb.auth.exchangeCodeForSession(window.location.href);
            url.searchParams.delete('code');
            url.searchParams.delete('state');
            history.replaceState(null, '', url.pathname + (url.searchParams.size ? `?${url.searchParams.toString()}` : '') + url.hash);
          }
        } catch {}

        // Hydrate from Supabase session only (no module-level token cache)
        const { data } = await sb.auth.getSession();
        setFromSession(data.session ?? null);

        if (data.session?.access_token) {
          const serverSession = await fetchServerSessionFromToken(data.session.access_token);
          // Auto-redirect on boot if already logged in - send users to editor (or saved)
          if (!hasBootRedirectedRef.current && bootPhaseRef.current) {
            hasBootRedirectedRef.current = true;
            postAuthNav();
          }
          if (serverSession) {
            setInitializing(false);
            bootPhaseRef.current = false;
            setReady(true);
            return;
          }
        } else {
          setFromSession(null);
          setSession(null);
          setAuthError(null);
        }
      } finally {
        setInitializing(false);
        bootPhaseRef.current = false;
        setReady(true);
        // Fallback cleanup in case no auth event fires but hash contains auth params
        setTimeout(stripSupabaseAuthFromHash, 1000);
      }
    })();

    const sb = getSupabase();
    // Cross-tab: listen for auth:changed and rehydrate from Supabase
    const off = onAuthChanged(async () => {
      try {
        const { data } = await sb.auth.getSession();
        setFromSession(data.session ?? null);
      } catch {}
    });

    unsub = sb.auth.onAuthStateChange(async (event, sess) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || event === 'PASSWORD_RECOVERY') {
        // Once we’re signed in (or token refreshed), scrub auth tokens from URL
        setTimeout(stripSupabaseAuthFromHash, 0);
      }
      const token = sess?.access_token;
      if (event === 'SIGNED_IN' && token) {
        setFromSession(sess ?? null);
        try { postAuthChanged(); } catch {}
        if (bootPhaseRef.current) {
          // Avoid double-redirect if boot already navigated
          if (!hasBootRedirectedRef.current) {
            setInitializing(true);
            await fetchServerSessionFromToken(token);
            try { clearAuthPending(); } catch {}
            postAuthNav();
            setInitializing(false);
            hasBootRedirectedRef.current = true;
          }
        } else {
          // Silent refresh post-boot; do not toggle initializing to avoid UI reboot
          await fetchServerSessionFromToken(token);
          try { clearAuthPending(); } catch {}
          try {
            // Broaden session-scoped invalidations after sign-in
            await Promise.all([
              (async () => { try { await (await import('@/lib/queryClient')).queryClient.invalidateQueries({ queryKey: ['/api/credits'] as any }); } catch {} })(),
              (async () => { try { await (await import('@/lib/queryClient')).queryClient.invalidateQueries({ queryKey: ['/api/projects'] as any }); } catch {} })(),
            ]);
          } catch {}
        }
        return;
      }
      if (event === 'TOKEN_REFRESHED') {
        setFromSession(sess ?? null);
        try { postAuthChanged(); } catch {}
        return;
      }
      if (event === 'SIGNED_OUT') {
        setFromSession(null);
        setSession(null);
        setAuthError(null);
        try { postAuthChanged(); } catch {}
        try {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['/api/credits'] as any }),
            queryClient.invalidateQueries({ queryKey: ['/api/projects'] as any }),
          ]);
        } catch {}
        try { clearAuthPending(); } catch {}
        try {
          go(routes.home());
        } catch {}
      }
    }) as any;

    return () => {
      try { unsub?.data?.subscription?.unsubscribe(); } catch {}
      try { off && off(); } catch {}
    };
  }, [setSession, setFromSession, setReady, go, postAuthNav]);

  const value = useMemo<AuthContextValue>(
    () => ({ initializing, user, session, defaultProjectId, authError }),
    [initializing, user, session, defaultProjectId, authError],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}


